import { inject, InjectionToken } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { combineLatest, distinctUntilChanged, filter, map, of, pipe, switchMap, tap } from 'rxjs';
import { AliasService } from 'src/app/authentication/alias/alias.service';
import { getIsNullable } from '../nullable-utils';
import { AuthService } from '../services/auth.service';
import { ViewAccessApiService, ViewAccessDto } from '../services/view-access-api.service';
import { ProjectStore } from './project.store';

interface ViewAccessState {
  viewAccess: ViewAccessDto[];
  isLoading: boolean;
}

const ViewAccessInitialState: ViewAccessState = {
  viewAccess: [],
  isLoading: false,
};

const ViewAccessStateToken = new InjectionToken<ViewAccessState>('ViewAccessState', {
  factory: () => ViewAccessInitialState,
});

export const ViewAccessStore = signalStore(
  { providedIn: 'root' },
  withState(() => inject(ViewAccessStateToken)),
  withMethods((store, apiService = inject(ViewAccessApiService)) => ({
    loadByProjectId: rxMethod<number | undefined>(
      pipe(
        takeUntilDestroyed(),
        tap(() => patchState(store, { isLoading: true })),
        switchMap(projectId => {
          return (getIsNullable(projectId) ? of([]) : apiService.getViewAccess({ projectId })).pipe(
            tapResponse({
              next: viewAccess => patchState(store, { viewAccess }),
              error: console.error,
              finalize: () => patchState(store, { isLoading: false }),
            }),
          );
        }),
      ),
    ),
  })),
  withHooks({
    // This triggers self-initialization of the viewAccess value, and keeps it updated on projectId changes
    onInit(
      store,
      projectStore = inject(ProjectStore),
      authService = inject(AuthService),
      aliasService = inject(AliasService),
    ) {
      const reloadSignal$ = combineLatest([
        toObservable(projectStore.currentProjectId),
        toObservable(aliasService.alias),
        authService.user$.pipe(
          distinctUntilChanged(),
          filter(user => !!user.email), // Ensure user is valid, when we sign out we set the email to ''
        ),
      ]).pipe(map(([projectId]) => projectId));
      store.loadByProjectId(reloadSignal$);
    },
  }),
);
